import type {
  PageNumber,
  PDFMergeFunction,
  PDFMergeInput,
} from "@pdfking/renderer-api"
import type { PdfiumApi } from "@pdfking/renderer-wasm-pdfium"
import type { Bytes } from "@pdfking/tfunc"
import { ToBlob } from "@pdfking/tfunc"

import { CloseDocument } from "../functions/CloseDocument"
import { ReadDocument } from "../functions/ReadDocument"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

const PATCH_BUFFER_SIZE = (1024 * 1024) as Bytes // 1MB
const XREF_BUFFER_SIZE = (64 * 1024) as Bytes // 64KB for xref entries

// PDF header + Catalog (using \r\n like pdfium)
const PDF_HEADER =
  "%PDF-1.5\r\n1 0 obj <</Type/Catalog/Pages 2 0 R>> endobj\r\n"

// Offset of object 1 (Catalog) in header: "%PDF-1.5\r\n" = 10 bytes
const CATALOG_OFFSET = 10

interface PageObjEntry {
  readonly page: PageNumber
  readonly obj: number
}

// Format a single xref entry (20 bytes): "OOOOOOOOOO 00000 n\r\n" (like pdfium)
const formatXrefEntry = (offset: number): string =>
  `${String(offset).padStart(10, "0")} 00000 n\r\n`

async function* PDFMergeImpl(
  api: PdfiumApi,
  pages: AsyncIterable<PDFMergeInput> | Iterable<PDFMergeInput>
): AsyncGenerator<Blob> {
  // Yield PDF header and Catalog
  yield new Blob([PDF_HEADER])

  // Track state
  let lastObjNum = 2 // Catalog is obj 1, Pages is obj 2, pages start at 3
  let documentSize = PDF_HEADER.length
  const pageEntries: PageObjEntry[] = []
  const xrefChunks: Blob[] = [] // Raw xref bytes from C++

  // Allocate WASM buffers
  const { pointer: patchPtr, free: freePatch } = api.malloc(PATCH_BUFFER_SIZE)
  const { pointer: xrefPtr, free: freeXref } = api.malloc(XREF_BUFFER_SIZE)
  const { pointer: objInfoPtr, free: freeObjInfo } = api.malloc(12 as Bytes)

  try {
    for await (const input of pages) {
      const reader = await ReadDocument(api, input)

      try {
        const pageCount = await api.call(
          "FPDF_GetPageCount",
          reader.doc_pointer
        )
        if (pageCount !== 1) {
          throw new Error(`Expected source to have 1 page, got ${pageCount}`)
        }

        // Export page objects - C++ writes xref entries in PDF format
        const bytesWritten = await api.safeCall(
          "PDFKING_ExportPageObjects",
          reader.doc_pointer,
          0,
          lastObjNum,
          documentSize,
          patchPtr,
          PATCH_BUFFER_SIZE as number,
          xrefPtr,
          XREF_BUFFER_SIZE as number,
          objInfoPtr
        )

        yield ToBlob([api.readBytes(patchPtr, bytesWritten as Bytes)])

        // Read object info: [pageObjNum, highestObjNum, xrefBytesWritten]
        const objInfoData = api.readBytes(objInfoPtr, 12 as Bytes)
        const objInfoView = new DataView(
          objInfoData.buffer,
          objInfoData.byteOffset,
          12
        )
        const newPageObjNum = objInfoView.getUint32(0, true)
        const highestObjNum = objInfoView.getUint32(4, true)
        const xrefBytesWritten = objInfoView.getUint32(8, true)

        // Accumulate raw xref bytes from C++
        xrefChunks.push(ToBlob(api.readBytes(xrefPtr, xrefBytesWritten as Bytes)))

        // Update tracking
        pageEntries.push({ page: input.page_number, obj: newPageObjNum })
        lastObjNum = highestObjNum
        documentSize += bytesWritten
      } finally {
        await CloseDocument(api, reader)
      }
    }

    // Write footer: Pages dict + xref + trailer
    if (pageEntries.length > 0) {
      const pagesOffset = documentSize
      const sortedPages = pageEntries.sort((a, b) => a.page - b.page)
      const kids = sortedPages.map(e => `${e.obj} 0 R`).join(" ")
      const pagesObj = `2 0 obj <</Type/Pages/Count ${sortedPages.length}/Kids[${kids}]>> endobj\r\n`

      // Pages dict
      yield new Blob([pagesObj])

      // xref header (objects 0, 1, 2) - using \r\n like pdfium
      yield new Blob([
        `xref\r\n`,
        `0 1\r\n0000000000 65535 f\r\n`,
        `1 ${lastObjNum}\r\n`,
        formatXrefEntry(CATALOG_OFFSET),
        formatXrefEntry(pagesOffset),
      ])

      // xref entries from C++ (already formatted as raw bytes)
      yield* xrefChunks

      // trailer - using \r\n like pdfium
      const xrefTableOffset = documentSize + pagesObj.length
      yield new Blob([
        `trailer\r\n<</Size ${lastObjNum + 1}/Root 1 0 R>>\r\n`,
        `startxref\r\n${xrefTableOffset}\r\n%%EOF\r\n`,
      ])
    }
  } finally {
    freePatch()
    freeXref()
    freeObjInfo()
  }
}

export const PDFMergeExperimental: PDFMergeFunction = pages =>
  ProvisionPdfium(api => PDFMergeImpl(api, pages))
